/**
 * Core typing model shared by client (prediction/UI) and server (authoritative).
 * Pure functions over a plain state object so it is trivial to test and serialise.
 */

export interface RaceConfig {
  /** Sentences per race. */
  sentenceCount: number;
  /** Milliseconds the vehicle visibly stalls after a typo (cosmetic; the real cost is fixing it). */
  typoStallMs: number;
  /** Correct keystrokes in a row required for a nitro bonus. */
  streakLength: number;
  /** Progress bonus (in characters) awarded per completed streak. */
  streakBonusChars: number;
  /** Treat "e" as a match for "é" (and similar), for language lessons on plain keyboards. */
  accentInsensitive?: boolean;
}

/** Strip diacritics so "é" can be typed as "e" on any keyboard. */
const FOLD_SPECIAL: Record<string, string> = { ß: 's', ł: 'l', Ł: 'L', ø: 'o', Ø: 'O', đ: 'd', Đ: 'D', æ: 'a', Æ: 'A', œ: 'o', Œ: 'O' };
export function foldAccent(ch: string): string {
  const special = FOLD_SPECIAL[ch];
  if (special) return special;
  return ch.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

export const DEFAULT_RACE_CONFIG: RaceConfig = {
  sentenceCount: 5,
  typoStallMs: 400,
  streakLength: 30,
  streakBonusChars: 6,
};

export interface Keystroke {
  /** Character expected at the cursor. */
  expected: string;
  /** Character actually typed (single char or 'Backspace'). */
  typed: string;
  /** Timestamp in ms (performance.now or Date.now, must be consistent within a race). */
  t: number;
  correct: boolean;
  /** Index into sentences, and char index within it. */
  sentence: number;
  index: number;
}

export interface TypingState {
  sentences: string[];
  totalChars: number;
  sentenceIndex: number;
  /** Correct chars committed in the current sentence. */
  cursor: number;
  /** Wrong characters typed after the cursor that must be backspaced. */
  errorBuffer: string;
  /** Correct characters committed across all completed sentences plus current cursor. */
  committedChars: number;
  bonusChars: number;
  streak: number;
  streaksCompleted: number;
  stallUntil: number;
  keystrokes: Keystroke[];
  correctCount: number;
  errorCount: number;
  backspaceCount: number;
  startedAt: number | null;
  finishedAt: number | null;
  config: RaceConfig;
}

export type KeyEvent = 'correct' | 'error' | 'backspace' | 'sentenceDone' | 'finished' | 'ignored' | 'nitro';

export function createTypingState(
  sentences: string[],
  config: RaceConfig = DEFAULT_RACE_CONFIG,
): TypingState {
  return {
    sentences,
    totalChars: sentences.reduce((n, s) => n + s.length, 0),
    sentenceIndex: 0,
    cursor: 0,
    errorBuffer: '',
    committedChars: 0,
    bonusChars: 0,
    streak: 0,
    streaksCompleted: 0,
    stallUntil: 0,
    keystrokes: [],
    correctCount: 0,
    errorCount: 0,
    backspaceCount: 0,
    startedAt: null,
    finishedAt: null,
    config,
  };
}

export function currentSentence(state: TypingState): string {
  return state.sentences[state.sentenceIndex] ?? '';
}

export function isFinished(state: TypingState): boolean {
  return state.finishedAt !== null;
}

/** 0..1 progress along the track. Bonus chars can push a player slightly ahead of raw typing. */
export function progressOf(state: TypingState): number {
  if (state.totalChars === 0) return 0;
  if (isFinished(state)) return 1;
  return Math.min(1, (state.committedChars + state.bonusChars) / state.totalChars);
}

/**
 * Apply a keystroke. `key` is a single printable character or 'Backspace'.
 * Returns the event so callers can play sounds / FX. Mutates state.
 */
export function applyKey(state: TypingState, key: string, now: number): KeyEvent {
  if (isFinished(state)) return 'ignored';
  if (state.startedAt === null) state.startedAt = now;

  const sentence = currentSentence(state);

  if (key === 'Backspace') {
    state.backspaceCount++;
    if (state.errorBuffer.length > 0) {
      state.errorBuffer = state.errorBuffer.slice(0, -1);
      return 'backspace';
    }
    // Backspacing correct text is allowed but costs progress.
    if (state.cursor > 0) {
      state.cursor--;
      state.committedChars--;
      state.streak = 0;
      return 'backspace';
    }
    return 'ignored';
  }

  if (key.length !== 1) return 'ignored';

  const expected = sentence[state.cursor] ?? '';
  const matches = key === expected || (state.config.accentInsensitive === true && foldAccent(key) === foldAccent(expected));
  const correct = state.errorBuffer.length === 0 && matches;

  state.keystrokes.push({
    expected,
    typed: key,
    t: now,
    correct,
    sentence: state.sentenceIndex,
    index: state.cursor + state.errorBuffer.length,
  });

  if (!correct) {
    // Every key after a typo joins the error buffer until it is backspaced away.
    // Cap it so a runaway typist doesn't have to delete 40 chars.
    if (state.errorBuffer.length < 8) state.errorBuffer += key;
    state.errorCount++;
    state.streak = 0;
    state.stallUntil = now + state.config.typoStallMs;
    return 'error';
  }

  state.cursor++;
  state.committedChars++;
  state.correctCount++;
  state.streak++;

  let event: KeyEvent = 'correct';
  if (state.streak >= state.config.streakLength) {
    state.streak = 0;
    state.streaksCompleted++;
    state.bonusChars += state.config.streakBonusChars;
    event = 'nitro';
  }

  if (state.cursor >= sentence.length) {
    state.sentenceIndex++;
    state.cursor = 0;
    state.errorBuffer = '';
    if (state.sentenceIndex >= state.sentences.length) {
      state.finishedAt = now;
      return 'finished';
    }
    return 'sentenceDone';
  }
  return event;
}

/** Gross words per minute over the whole race so far (5 chars = 1 word). */
export function wpmOf(state: TypingState, now: number): number {
  if (state.startedAt === null) return 0;
  const end = state.finishedAt ?? now;
  const minutes = (end - state.startedAt) / 60000;
  if (minutes <= 0) return 0;
  return state.correctCount / 5 / minutes;
}

/** WPM over the last `window` keystrokes: what the speedometer shows. */
export function rollingWpmOf(state: TypingState, now: number, window = 12): number {
  const ks = state.keystrokes;
  if (ks.length < 2) return 0;
  const recent = ks.slice(-window);
  const first = recent[0]!;
  const last = recent[recent.length - 1]!;
  // If the player has paused, decay towards zero.
  const span = Math.max(last.t - first.t, now - first.t);
  if (span <= 0) return 0;
  const correct = recent.filter((k) => k.correct).length;
  return correct / 5 / (span / 60000);
}

/** 0..1 accuracy: correct keystrokes over all character keystrokes. */
export function accuracyOf(state: TypingState): number {
  const total = state.correctCount + state.errorCount;
  return total === 0 ? 1 : state.correctCount / total;
}

export interface TypingSummary {
  wpm: number;
  accuracy: number;
  correct: number;
  errors: number;
  backspaces: number;
  streaks: number;
  durationMs: number;
}

export function summarise(state: TypingState, now: number): TypingSummary {
  const end = state.finishedAt ?? now;
  return {
    wpm: Math.round(wpmOf(state, now) * 10) / 10,
    accuracy: Math.round(accuracyOf(state) * 1000) / 1000,
    correct: state.correctCount,
    errors: state.errorCount,
    backspaces: state.backspaceCount,
    streaks: state.streaksCompleted,
    durationMs: state.startedAt === null ? 0 : end - state.startedAt,
  };
}
